import { readonly, ref } from "vue";

import type { Master } from "@/entities/master";
import { masterApi } from "@/entities/master";
import type { AppError } from "@/shared/api";

/**
 * Стан host-load майстра (spec §5.1). `loading` поки триває GET, `ready` —
 * успіх, `notFound` — 404 / `invalid_id` (deep-link на видаленого), `error` —
 * network/server (панель + retry).
 */
export type MasterDetailStatus = "loading" | "ready" | "notFound" | "error";

/**
 * Host-composable tab-host'а (spec §5.1, §4.4a). Робить **один** GET майстра —
 * server-authoritative джерело ідентифікаційного хедера (Avatar + ПІБ) і
 * `:master` для вкладки «Фото». Профіль-вкладка вантажиться сама (self-load,
 * §4.4) — host її не годує. `setMaster`/`patchPhoto` застосовують результати
 * подій вкладок (`@saved`/`@changed`), щоб хедер реактивно оновлювався без
 * повторного fetch. Навігації/announce не робить — це консюмер (widget/page).
 */
export function useMasterDetail() {
  const status = ref<MasterDetailStatus>("loading");
  const master = ref<Master | null>(null);

  async function load(id: string): Promise<void> {
    status.value = "loading";
    try {
      master.value = await masterApi.getMaster(id);
      status.value = "ready";
    } catch (e) {
      const err = e as AppError;
      // 404 master_not_found / 400 invalid_id → майстра вже немає (панель).
      // Інше (network/server/unknown) → error-панель + retry (лишитись на роуті).
      status.value
        = err.kind === "notFound"
          || (err.kind === "validation" && err.code === "invalid_id")
          ? "notFound"
          : "error";
    }
  }

  /** Застосувати свіжий профіль із події `@saved` вкладки «Профіль» (ПІБ-хедер). */
  function setMaster(m: Master): void {
    master.value = m;
  }

  /** Застосувати результат фото-мутації (`@changed`) — оновити лише аватар хедера. */
  function patchPhoto(url: string | null): void {
    if (master.value) {
      master.value = { ...master.value, photoUrl: url };
    }
  }

  /** Позначити майстра видаленим (bubble notFound з вкладки) без обходу API стану. */
  function markNotFound(): void {
    status.value = "notFound";
  }

  return { status: readonly(status), master, load, setMaster, patchPhoto, markNotFound };
}
